import { useCallback, useEffect, useState } from 'react'

import { LoaderCircle, SaveIcon } from 'lucide-react'
import { Row, Col, Label, FormGroup, Button, Input, SelectMulti, Select, FormFeedback, ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent, DefaultPopover, SimpleAlert, Textarea } from 'twyne-new.ui'

import { useReadBlockLevel } from '@/components/builder/utils/block-level'
import { useSerialize } from '@/components/builder/utils/serialize-block'
import { useSnapshotBlock } from '@/components/builder/utils/snapshot-block'
import { useBuilderStore } from '@/components/builder/utils/store'
import { uploadBlockPreview } from '@/components/builder/utils/upload-preview'

export function ActionSave({ nodeId, isActive }) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!isActive && open) {
      setOpen(false)
    }
  }, [isActive, open])

  if (!isActive) {
    return null
  }

  return <DefaultPopover
    trigger={<SaveIcon size={12} />}
    wrapperProps={{ open, onOpenChange: setOpen }}
    triggerProps={{ asChild: true }}
    contentProps={{ side: 'bottom', align: 'center', className: 'mt-1 w-70' }}
  >
    <Form nodeId={nodeId} onClose={() => setOpen(false)} />
  </DefaultPopover>
}

export function OptionSave({ nodeId }) {
  const [open, setOpen] = useState(false)

  const preventOutside = event => {
    event.preventDefault()
  }

  return <ContextMenuSub open={open} onOpenChange={setOpen}>
    <ContextMenuSubTrigger>Save</ContextMenuSubTrigger>
    <ContextMenuSubContent className='p-4 w-70' onFocusOutside={preventOutside}>
      <Form nodeId={nodeId} onClose={() => setOpen(false)} />
    </ContextMenuSubContent>
  </ContextMenuSub>
}

function Form({ nodeId, onClose }) {
  const { block: { save }, fetch, siteSelected: { id: siteId }, blocks: { reset }, templateBlock: { fetchBlocksTags } } = useBuilderStore()
  const serialize = useSerialize(nodeId)
  const snapshot = useSnapshotBlock(nodeId)
  const blockLevel = useReadBlockLevel()

  const [loading, setLoading] = useState(false)
  const [tagOptions, setTagOptions] = useState([])
  const [tags, setTags] = useState([])
  const [error, setError] = useState({})

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [level, setLevel] = useState('site')

  const loadOptions = useCallback(async () => {
    const tags = await fetchBlocksTags()
    setTagOptions(tags.map(tag => ({ value: tag, label: tag })))
  }, [fetchBlocksTags])

  useEffect(() => {
    loadOptions()
  }, [loadOptions])

  const validate = () => {
    if (!name) {
      setError({ name: 'Name is required' })
      return false
    }

    if (level === 'organization' && blockLevel?.getLevel() === 'site') {
      setError({ level: 'Incompatible level' })
      return false
    }

    return true
  }

  const handleName = event => {
    setName(event.target.value)
    setError({})
  }

  const handleLevel = option => {
    setLevel(option.value)
    setError({})
  }

  const handleUpload = async file => file ? uploadBlockPreview(fetch, file, { siteId }) : null

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setLoading(true)
    try {
      const definition = serialize()
      const tagsValues = tags.map(tag => tag.value)
      const image = await snapshot()

      const file = await handleUpload(image)

      await save({
        name,
        definition,
        meta: { file, description: description.trim() },
        tags: tagsValues,
        level,
      })

      reset()

      onClose()
    } catch (error) {
      setError({ save: 'Block not saved' })
    }

    setLoading(false)
  }

  return <Row>
    <Col size='12'>
      <FormGroup>
        <Label>Name</Label>
        <Input name='name' value={name} onChange={handleName} error={error.name} />
        {error.name && <FormFeedback>{error.name}</FormFeedback>}
      </FormGroup>
    </Col>
    <Col size='12'>
      <FormGroup>
        <Label>Description</Label>
        <Textarea name='description' value={description} onChange={event => setDescription(event.target.value)}
          placeholder='What this block is for and when to use it' rows={3} />
      </FormGroup>
    </Col>
    <Col size='12'>
      <FormGroup>
        <Label>Level</Label>
        <Select value={level} onChange={handleLevel} options={LEVELS} creatable />
        {error.level && <FormFeedback>{error.level}</FormFeedback>}
      </FormGroup>
    </Col>
    <Col size='12'>
      <FormGroup>
        <Label>Tags</Label>
        <SelectMulti value={tags} options={tagOptions} onChange={setTags}
          placeholder='Type to create a tag' creatable />
      </FormGroup>
    </Col>
    <Col size='12'>
      <Button block size='sm' color='success' onClick={handleSubmit} disabled={loading}>
        {loading ? <LoaderCircle className='animate-spin' size={16} /> : 'Save'}
      </Button>
    </Col>
    {error.save ? <Col size='12'>
      <SimpleAlert className='m-0 py-2 px-3' variant='danger'>{error.save}</SimpleAlert>
    </Col> : null}
  </Row>
}

const LEVELS = [
  { value: 'site', label: 'Site' },
  { value: 'organization', label: 'Organization' },
]
